import {
	carrierName,
	closedReasonSchema,
	contractSuretyTypeSchema,
	currencySchema,
	isoDateTimeSchema,
	suretyBondId,
	suretyQuoteId,
	suretyQuoteStatusSchema,
	usStateOrTerritorySchema,
} from "@inrev/common";
import * as z from "zod";
import { listData, listInput } from "../schemas/fragments";
import { defineTool } from "../tool-definition";

const bondRequestSummaryBase = {
	bondRequestId: suretyQuoteId,
	status: suretyQuoteStatusSchema,
	bondAmount: currencySchema.nullable(),
	carrierName: carrierName.nullable(),
	projectName: z.string().nullable(),
	projectCity: z.string().nullable(),
	projectState: usStateOrTerritorySchema.nullable(),
	bondId: suretyBondId.nullable(),
	submittedAt: isoDateTimeSchema.nullable(),
	reviewedAt: isoDateTimeSchema.nullable(),
	expiresAt: isoDateTimeSchema.nullable(),
	closedReason: closedReasonSchema.nullable(),
};

const bondRequestSummarySchema = z.discriminatedUnion("contractSuretyType", [
	z.strictObject({
		...bondRequestSummaryBase,
		contractSuretyType: z.literal("bid"),
		bidAmount: currencySchema,
	}),
	z.strictObject({
		...bondRequestSummaryBase,
		contractSuretyType: z.literal("bid_to_final"),
		contractAmount: currencySchema,
		bidBondId: suretyBondId,
		bidBondNumber: z.string().nullable(),
	}),
	z.strictObject({
		...bondRequestSummaryBase,
		contractSuretyType: z.literal("final"),
		contractAmount: currencySchema.nullable(),
	}),
]);

export const listBondRequests = defineTool({
	name: "list_bond_requests",
	description:
		"Bond requests on the account, newest first. `pending`, `review`, `approved` and `accepted` requests are open: work the account wants to do. `bound` requests became the bond in `bondId`. `contractSuretyType` is `bid`, `bid_to_final` or `final`, and the fields after it differ by type: a bid request carries `bidAmount`, the bid; a final or bid-to-final request `contractAmount`, and a bid-to-final `bidBondId` and `bidBondNumber`, the bid bond it replaces. `bondAmount` is the bond requested; on a bid request that is the bid bond's face amount, a share of the bid. Never add these rows up for exposure: call `derive_open_exposure`. Review reasons, project detail, obligees and bid results are on `get_bond_request`.",
	label: "Bond requests",
	contexts: ["account"],
	implementation: "api",
	audience: "model",
	input: listInput({
		status: suretyQuoteStatusSchema.optional(),
		contractSuretyType: contractSuretyTypeSchema.optional(),
	}),
	data: listData(bondRequestSummarySchema),
});
